import React from 'react';
import { FC } from 'react';
import { safeHtmlLinks } from '@/utils/links/safeHref';

interface HeaderCell {
    text: string;
    colspan?: number;
    rowspan?: number;
}

interface TableCell {
    text: string;
    colspan?: number;
    rowspan?: number;
}

interface TableRow {
    [key: string]: TableCell[];
}

interface CustomTableProps {
    thead: HeaderCell[];
    tbody: TableRow[];
}

/**
 * Table from the editor's custom table block. Cells keep a minimum width on desktop, so a wide
 * table scrolls sideways (as on phones) instead of squeezing each cell to a word or two per line.
 */
const CustomTable: FC<CustomTableProps> = ({ thead, tbody }) => {
    const renderRow = (rowObject: TableRow): React.JSX.Element => {
        const rowKey = Object.keys(rowObject)[0];
        const cells = rowObject[rowKey];

        return (
            <tr
                key={rowKey}
                className='transition-all hover:bg-progress/10 bg-[#F5F8FC] dark:bg-primary w-full dark:text-white/80'
            >
                {cells.map((cell, index) => (
                    <td
                        colSpan={cell.colspan}
                        rowSpan={cell.rowspan}
                        className='border border-primary/10 px-3 py-2 leading-7 dark:border-white/10 max-lg:whitespace-nowrap lg:min-w-[8.5rem] font-light text-sm'
                        key={index}
                    >
                        <div dangerouslySetInnerHTML={{ __html: safeHtmlLinks(cell.text) }} />
                    </td>
                ))}
            </tr>
        );
    };

    return (
        <div className='!my-10 w-full max-w-full min-w-0 overflow-x-auto rounded-2xl border border-primary/30 dark:border-white/30 dark:bg-white/10 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-white/10'>
            <table className='m-0 w-full min-w-full border-collapse px-1 lg:table-auto'>
                <thead className='border-t-4 border-progress rounded-2xl'>
                    <tr>
                        {thead.map((header, index) => (
                            <th
                                key={index}
                                colSpan={header.colspan}
                                rowSpan={header.rowspan}
                                className='whitespace-nowrap bg-white dark:bg-white/10 p-2 text-start text-progress dark:text-white font-medium border border-primary/10'
                            >
                                <div
                                    dangerouslySetInnerHTML={{ __html: safeHtmlLinks(header.text) }}
                                />
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>{tbody.map((row) => renderRow(row))}</tbody>
            </table>
        </div>
    );
};

export default CustomTable;
